import type { GeographicMarketData as MarketData, GeographicMarketTimeSeries as MarketDataTimeSeries } from '@einwert/valuation-api-client'
import type { StructureUsageType } from '@einwert/valuation-api-client'
import { Box, GridLegacy as Grid, Stack } from '@mui/material'
import { BaseChipPropTypes, ChipList, InfoTooltip, tokens } from 'ComponentLibrary'

import { entries, groupBy, isEmpty, values } from 'lodash'
import { getTimeSeriesTopicTypeLabels, getTopicCategories } from 'ClientPortal/src/pages/MarketData/constants'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { AssetLocation, GroupedSeriesEntries, MarketDataCategory } from 'ClientPortal/src/pages/MarketData/types'
import { MarketDataNoData } from 'ClientPortal/src/pages/MarketData/Placeholder/MarketDataNoData'
import { MarketDataNoSelection } from 'ClientPortal/src/pages/MarketData/Placeholder/MarketDataNoSelection'
import { MarketDataLoadingSkeleton } from 'ClientPortal/src/pages/MarketData/Placeholder/MarketDataLoadingSkeleton'
import { MarketDataExpandable } from 'ClientPortal/src/pages/MarketData/Explorer/MarketDataExpandable'
import { useMarketDataAdapter } from 'ClientPortal/src/pages/MarketData/Explorer/MarketDataAdapter.data'
import { MAX_COLUMN_WIDTH } from 'ComponentLibrary/src/templates/PageTemplate/constants'
import { useFlags } from 'ClientPortal/src/context/FeatureFlagContext'
import { Typography } from 'ComponentLibrary/src/piazza/text'
import { useTranslation } from 'react-i18next'

export type MarketDataExplorerProps = {
    marketData?: MarketData
    isMarketDataPending: boolean
    selectedLocation?: AssetLocation
}

export const MarketDataExplorer = ({ marketData, isMarketDataPending, selectedLocation }: MarketDataExplorerProps) => {
    const { t } = useTranslation('pages', { keyPrefix: 'marketData' })
    const { mapAssetToMarketDataSeries } = useMarketDataAdapter()
    const { isMarketDataDemoDataEnabled } = useFlags()
    const topicCategories = useMemo(() => getTopicCategories(t), [t])
    const topicLabels = useMemo(() => getTimeSeriesTopicTypeLabels(t), [t])
    const [chips, setChips] = useState<BaseChipPropTypes<MarketDataCategory>[]>([])
    const toggleChip = (key: string) =>
        setChips((prev) => prev.map((chip) => (chip.value === key ? { ...chip, selected: !chip.selected } : chip)))

    const allSeries = useMemo(() => marketData?.real_estate_data.time_series ?? [], [marketData])
    const selectedCategories = chips.filter((chip) => chip.selected).map((chip) => chip.value)
    const selectedTopics = selectedCategories.flatMap((selectedCategory) => topicCategories[selectedCategory].topics)
    const selectedSeries = allSeries.filter((serie) => selectedTopics.includes(serie.topic))

    const groupSeriesByTopic = (timeSeries: MarketDataTimeSeries[]) => groupBy(timeSeries, 'topic')
    const sortSeriesByCategory = (timeSeries: MarketDataTimeSeries[]) =>
        values(topicCategories).flatMap((category) => timeSeries.filter((ts) => category.topics.includes(ts.topic)))

    const structureTypes = useMemo(
        () =>
            selectedLocation?.uuid
                ? selectedLocation.economic_units.flatMap((unit) => unit.structures.map((structure) => structure.usage_type))
                : [],
        [selectedLocation]
    )

    const getEnabledCategories = useCallback(
        () =>
            entries(topicCategories)
                .filter(([_, value]) => allSeries.find((serie) => value.topics.includes(serie.topic)))
                .map(([key]) => key),
        [allSeries, topicCategories]
    )

    const isStructureTypeWithoutDefault = useCallback(
        (structureType: StructureUsageType) =>
            values(topicCategories).every((value) => !value.defaultForStructures.includes(structureType)),
        [topicCategories]
    )

    const getInitialChips = useCallback(
        (enabledCategories: string[]) =>
            entries(topicCategories).map(([key, value]) => ({
                label: value.title,
                value: key as MarketDataCategory,
                selected:
                    isEmpty(structureTypes) || structureTypes.every(isStructureTypeWithoutDefault)
                        ? enabledCategories.at(0) === key
                        : structureTypes.some((structureType) => value.defaultForStructures.includes(structureType)),
                disabled: !enabledCategories.includes(key),
            })),
        [isStructureTypeWithoutDefault, structureTypes, topicCategories]
    )

    useEffect(() => {
        const enabledCategories = getEnabledCategories()
        const initialChips = getInitialChips(enabledCategories)
        setChips(initialChips)
    }, [getEnabledCategories, getInitialChips])

    if (!selectedLocation) {
        return <MarketDataNoSelection />
    }

    if (isMarketDataPending) {
        return <MarketDataLoadingSkeleton />
    }

    return (
        <Stack direction="column" gap={tokens.SizingSpacing300}>
            <Stack direction="row" gap={tokens.SizingSpacing100} alignItems="baseline">
                <Typography variant="headline1" color={tokens.ColorTextBoldest}>
                    {t('explorer.title')}
                </Typography>
                {isMarketDataDemoDataEnabled && (
                    <Stack direction="row" alignItems="center" gap={tokens.SizingSpacing050}>
                        <Typography variant="headline5" color={tokens.ColorBorderInformative}>
                            {t('explorer.demoLabel')}
                        </Typography>
                        <InfoTooltip text={t('explorer.demoTooltip')} />
                    </Stack>
                )}
            </Stack>
            {!isEmpty(allSeries) && (
                <Box zIndex={1}>
                    <ChipList chips={chips} toggleChip={toggleChip} />
                </Box>
            )}
            {isEmpty(selectedSeries) ? (
                <MarketDataNoData />
            ) : (
                <Grid container maxWidth={`calc(${MAX_COLUMN_WIDTH * 2}px + ${tokens.SizingSpacing500})`} spacing={tokens.SizingSpacing500}>
                    {(entries(groupSeriesByTopic(sortSeriesByCategory(selectedSeries))) as GroupedSeriesEntries).map(([topic, group]) => (
                        <MarketDataExpandable
                            title={topicLabels[topic]}
                            isDemo={isMarketDataDemoDataEnabled}
                            key={`${topic}-${JSON.stringify(selectedLocation?.address?.coordinates)}`}
                            series={[...group, ...mapAssetToMarketDataSeries({ topic, asset: selectedLocation })]}
                        />
                    ))}
                </Grid>
            )}
        </Stack>
    )
}
